import SubLabel from "components/Atoms/Sublabel";
import React from "react";
import { type Props, components, createFilter } from "react-select";
import { Loader } from "semantic-ui-react";
import { focusOnMount } from "utilities/reactUtils";
import { useDebounce } from "utilities/usehooks";
import { type CommonReactSelectWrapperProps } from "./models";
import { PhosphorMagnifyingGlass, PhosphorCaretDown, PhosphorX } from "components/Atoms/PhosphorIcon";

// We don't care about TOption here
// If we make this generic, the typings will get a lot more complicated to support multi select dropdowns
type WrapperProps = CommonReactSelectWrapperProps<unknown, false, any>;

export interface UseSearchQuery {
  searchQuery: string
  setSearchQuery: (value: string) => void
  isDebouncing: boolean
  nonDebouncedSearchQuery: string
}
export const useSearchQuery = (initialSearchQuery = ""): UseSearchQuery => {
  const [searchQuery, setSearchQuery] = React.useState(initialSearchQuery);
  const debouncedSearchQuery = useDebounce(searchQuery);

  return {
    searchQuery: debouncedSearchQuery,
    setSearchQuery,
    isDebouncing: searchQuery !== debouncedSearchQuery,
    nonDebouncedSearchQuery: searchQuery
  };
};

export const useReactSelectDropdown = (
  props: WrapperProps,
  hasValidatorError: boolean = undefined,
) => {
  const mounted = React.useRef(false);

  const noResultsFound = RESX.GeneralLabels.NoResultsFound;
  const hasCustomOptions = props.renderOption != null;

  const config = props.config ?? {};

  const hasError = hasValidatorError ?? config.hasError;

  const reactSelectProps: Props<unknown> = {
    placeholder: config?.placeholder ?? "",
    noOptionsMessage: () => noResultsFound,

    defaultValue: null,
    getOptionValue: (opt) => opt == null ? undefined : props.getValue(opt)?.toString(),
    getOptionLabel: props.getLabel,
    filterOption: props.getSearchString
      ? createFilter({ stringify: opt => props.getSearchString(opt.data) })
      : undefined,

    value: props.value,
    onChange: props.onChange,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        props.onEsc?.();
      }
    },
    options: [],

    formatOptionLabel: props.renderOption,

    tabSelectsValue: false,

    openMenuOnFocus: config.openMenuOnFocus ?? false,
    menuShouldScrollIntoView: false,
    menuPlacement: "auto",

    isClearable: config.isClearable,
    isSearchable: config.isSearchable ?? false,
    isDisabled: config.isDisabled,
    isMulti: false,
    isLoading: config.isLoading,
    className: config.classes,
    controlShouldRenderValue: config.shouldRenderSelectedValue ?? true,
    menuPortalTarget: document.body,
    menuPosition: "absolute",

    loadingMessage: () => RESX.GeneralLabels.Searching,

    components: {
      LoadingIndicator: () => <div className="flex justify-center align-center">
        <Loader inline active size="tiny" />
      </div>,
      MenuPortal: CustomMenuPortal,
      DropdownIndicator: innerProps => (
        components.DropdownIndicator && (
          <components.DropdownIndicator {...innerProps}>
            {config.isSearchable && <PhosphorMagnifyingGlass iconWeight="bold" iconSize="small" />}
            {!config.isSearchable && <PhosphorCaretDown iconWeight="bold" iconSize="small" />}
          </components.DropdownIndicator>
        )
      ),
      ClearIndicator: innerProps => (
        <components.ClearIndicator {...innerProps}>
          <PhosphorX iconWeight="bold" iconSize="small" />
        </components.ClearIndicator>
      ),
      IndicatorSeparator: () => null,
      NoOptionsMessage: noOptsProps => {
        return <components.NoOptionsMessage {...noOptsProps}>
          {noResultsFound}
        </components.NoOptionsMessage>;
      },

      SingleValue: (singleValueProps) => (<components.SingleValue {...singleValueProps}>
        {props.renderSelectedValue != null && props.renderSelectedValue(singleValueProps.data)}
        {props.renderSelectedValue == null && <>
          <div>{props.getLabel(singleValueProps.data)}</div>
        </>}
      </components.SingleValue>),

      // Test the GooglePlacesDropdown if you enable this
      // Without memoizing this, the "input" will be re-rendered on every key press
      // This can cause it to lose focus after every key press
      // Input: props => (
      //     <components.Input {...props} />
      // ),

      ...(!hasCustomOptions
        ? {
          Option: innerProps => (
            <components.Option
              {...innerProps}
              className={`${innerProps.className ?? ""} ${innerProps.isSelected ? "react-select-selected-item" : ""}`}
            >
              <div>{props.getLabel(innerProps.data)}</div>
              {props.getSubLabel && <SubLabel small>
                {props.getSubLabel(innerProps.data)}
              </SubLabel>}
            </components.Option>
          )
        }
        : {}),

      MultiValue: innerProps => (
        <components.MultiValue {...innerProps}>
          <div>{props.getLabel(innerProps.data)}</div>
        </components.MultiValue>
      )
    },

    theme: (theme) => ({
      ...theme,
      colors: {
        ...theme.colors,
        primary: "var(--primary-color)",
        primary25: "var(--form-option-hover)",
        primary50: "var(--form-option-hover)",
        neutral80: "var(--text-color-light)",
      }
    }),

    styles: {
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
      control: (base, state) => {
        let backgroundColor = "var(--form-bg)";
        let hoverBackgroundColor = "var(--form-bg-hover)";
        if (hasError) backgroundColor = hoverBackgroundColor = "var(--form-error)";
        if (state.isDisabled) backgroundColor = hoverBackgroundColor = "var(--form-disabled)";
        // Inset so the stroke stays inside the field and doesn't change its size
        const focusStroke = `inset 0 0 0 1px ${hasError ? "var(--form-error-stroke)" : "var(--form-stroke)"}`;

        return {
          ...base,
          backgroundColor,
          "&:hover": {
            backgroundColor: hoverBackgroundColor,
            boxShadow: state.isFocused ? focusStroke : "inset 0 0 0 1px var(--form-stroke-hover)",
          },
          border: "none",
          boxShadow: state.isFocused ? focusStroke : "none",
          borderRadius: "var(--radius-medium)",
          borderBottomLeftRadius: state.menuIsOpen ? "0" : "var(--radius-medium)",
          borderBottomRightRadius: state.menuIsOpen ? "0" : "var(--radius-medium)",
          alignItems: "normal",
          minHeight: 0,
          cursor: "default",
          transition: "background-color 100ms, box-shadow 100ms",
        };
      },
      valueContainer: (base) => ({
        ...base,
        alignItems: "initial",
        padding: "8px 0px 8px 8px",
        lineHeight: "16px"
      }),
      indicatorsContainer: (base) => ({
        ...base,
        alignItems: "center",
        gap: 8,
        padding: "0px 8px",
      }),
      clearIndicator: (base) => ({
        ...base,
        padding: 0,
        color: "var(--text-color-light)",
        cursor: "pointer",
        "&:hover": {
          color: "var(--hover-color)"
        },
      }),
      multiValue: (base) => ({
        ...base,
        borderRadius: 32,
        backgroundColor: "var(--removable-label-bg)",
        display: "flex",
        alignItems: "center",
        gap: 4,
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: "var(--text-color)"
      }),
      multiValueRemove: (base) => ({
        ...base,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--text-color)",
        cursor: "pointer",
        width: 18,
        height: 18,
        marginRight: 4,
        paddingLeft: 2,
        paddingRight: 2,
        backgroundColor: "var(--removable-label-inner-bg)",
        borderRadius: 9,
        "&:hover": {
          color: "var(--hover-color)",
          backgroundColor: "var(--removable-label-inner-bg)",
        }
      }),
      input: (base) => ({
        ...base,
        padding: 0,
        margin: 0,
        color: "var(--text-color)",
      }),
      placeholder: (base) => ({
        ...base,
        margin: 0,
        color: "var(--text-color-lighter)",
      }),
      menu: (base, state) => ({
        ...base,
        // Copied from the field by CustomMenuPortal, since the menu is rendered in document.body
        background: "var(--form-menu-bg)",
        borderRadius: state.placement === "bottom" ? "0em 0em 0.30769231rem 0.30769231rem" : "0.30769231rem 0.30769231rem 0em 0em",
        marginTop: 0,
        marginBottom: 0,
        boxShadow: "none",
        border: "none",
        top: state.placement === "bottom" ? "100%" : undefined,
        bottom: state.placement === "top" ? "100%" : undefined,
      }),
      dropdownIndicator: (base) => ({
        ...base,
        padding: 0,
        color: "var(--text-color-light)",
        "&:hover": {
          color: "var(--text-color-light)",
        },
        lineHeight: 1
      }),
      singleValue: (base, state) => ({
        ...base,
        margin: 0,
        color: state.isDisabled ? "var(--form-disabled-color)" : "var(--text-color)",
      }),
      option: (base, state) => {
        if (!state.isSelected) return base;

        // The selected option is the hover color layered twice, so it's a stronger version of the hover
        const selectedBackground = {
          backgroundColor: "var(--form-option-hover)",
          backgroundImage: "linear-gradient(var(--form-option-hover), var(--form-option-hover))",
        };

        return {
          ...base,
          ...selectedBackground,
          color: "var(--text-color)",
          ":active": selectedBackground,
        };
      }
    }
  };

  const ref = ref => {
    if (config.focusOnMount && !mounted.current) {
      focusOnMount(ref);
      mounted.current = true;
    }
    if (config.inputRef) config.inputRef.current = ref?.inputRef;
  };

  return {
    reactSelectProps,
    ref
  };
};

// We make it a separate component or else it will remount every render
// causing issues with scrolling on menu open
const CustomMenuPortal = props => {
  // The menu is rendered in document.body, so it copies the surface colors (panel, card, modal) from the field
  const copySurfaceColors = (el: HTMLDivElement) => {
    if (el == null) return;

    const fieldStyle = getComputedStyle(props.controlElement);
    el.style.setProperty("--form-menu-bg", fieldStyle.getPropertyValue("--form-menu-bg"));
    el.style.setProperty("--form-option-hover", fieldStyle.getPropertyValue("--form-option-hover"));
  };

  return <components.MenuPortal {...props} className="react-select-menu-portal">
    <div ref={copySurfaceColors}>{props.children}</div>
  </components.MenuPortal>
};
